import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { downloadFile } from "@/lib/download";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, FolderOpen, Wallet, CalendarDays, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { logger } from "@/lib/logger";
import { Link } from "react-router-dom";

function ClientLogo({ src, name }) {
  const [broken, setBroken] = useState(false);
  const initials = (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");
  if (src && !broken) {
    return (
      <img
        src={src}
        alt={name || "Client logo"}
        onError={() => setBroken(true)}
        className="w-24 h-24 md:w-32 md:h-32 rounded-2xl object-cover border border-border bg-white"
        data-testid="portal-client-logo"
      />
    );
  }
  return (
    <div
      className="w-24 h-24 md:w-32 md:h-32 rounded-2xl bg-gradient-to-br from-teal/20 to-foreground/10 text-foreground font-medium text-3xl flex items-center justify-center border border-border"
      data-testid="portal-client-logo"
    >
      {initials || "?"}
    </div>
  );
}

export default function PortalDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/portal/dashboard").then(({ data }) => setStats(data)).catch((e) => logger.error("portal dash", e));
    api.get("/portal/me").then(({ data }) => setMe(data)).catch((e) => logger.error("portal me", e));
  }, []);

  const fmt = (n) => `$${(n || 0).toLocaleString()}`;

  return (
    <div className="space-y-6" data-testid="portal-dashboard">
      <div className="flex items-start gap-4 flex-wrap">
        {/* Big client logo — clients want to see their own brand front-and-center */}
        {me?.client && (
          <ClientLogo
            src={me.client.logo_url || me.client.avatar_url}
            name={me.client.client_name || me.client.business_name}
          />
        )}
        <div className="flex-1 min-w-[240px]">
          <div className="text-xs uppercase tracking-[0.2em] text-teal mb-2">Welcome</div>
          <h1 className="heading text-3xl md:text-4xl font-semibold">
            Hello {user?.name?.split(" ")[0] || "there"}
          </h1>
          {me?.client && (
            <p className="text-sm text-muted-foreground mt-1">
              {me.client.client_name} {me.client.business_name ? `· ${me.client.business_name}` : ""}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-5 border-border shadow-none">
          <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Pending approval</div>
          <div className="text-3xl font-light tracking-tight mt-2">{stats?.files_pending_approval ?? "—"}</div>
          <Button asChild size="sm" variant="ghost" className="mt-3 text-teal hover:text-teal -ml-2">
            <Link to="/portal/files" data-testid="link-portal-files"><FolderOpen size={14} className="mr-1" /> Review now</Link>
          </Button>
        </Card>
        <Card className="p-5 border-border shadow-none">
          <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Approved files</div>
          <div className="text-3xl font-light tracking-tight mt-2">{stats?.files_approved ?? "—"}</div>
          <CheckCircle2 size={14} className="text-teal mt-3" />
        </Card>
        <Card className="p-5 border-border shadow-none">
          <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Upcoming posts</div>
          <div className="text-3xl font-light tracking-tight mt-2">{stats?.upcoming_posts ?? "—"}</div>
          <Button asChild size="sm" variant="ghost" className="mt-3 text-teal hover:text-teal -ml-2">
            <Link to="/portal/calendar"><CalendarDays size={14} className="mr-1" /> View calendar</Link>
          </Button>
        </Card>
        <Card className="p-5 border-border shadow-none">
          <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Outstanding</div>
          <div className="text-3xl font-light tracking-tight mt-2">{fmt(stats?.unpaid_amount)}</div>
          <Button asChild size="sm" variant="ghost" className="mt-3 text-teal hover:text-teal -ml-2">
            <Link to="/portal/invoices"><Wallet size={14} className="mr-1" /> View invoices</Link>
          </Button>
        </Card>
      </div>

      <Card className="p-5 border-border shadow-none">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Content Calendar</div>
            <div className="text-sm mt-1">Download a printable copy or spreadsheet for your records.</div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="border-teal/40 text-teal hover:bg-teal/10 hover:text-teal" data-testid="portal-cal-pdf"
              onClick={() => downloadFile(`${API}/portal/calendar.pdf`, "content-calendar.pdf")}>
              <Download size={14} className="mr-1" /> PDF
            </Button>
            <Button size="sm" variant="outline" className="border-teal/40 text-teal hover:bg-teal/10 hover:text-teal" data-testid="portal-cal-xlsx"
              onClick={() => downloadFile(`${API}/portal/calendar.xlsx`, "content-calendar.xlsx")}>
              <Download size={14} className="mr-1" /> Excel
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
